export const DEFAULT_COLOR = '#a8c030';

export type WidgetTheme = {
    primary: string;
    primaryHover: string;
    primaryBorder: string;
    primaryContrast: string;
    primaryLight: string;
    primaryActive: string;
    primaryDark: string;
    primaryShadow: string;
    primaryShadowHover: string;
    primaryRing: string;
};

const DEFAULT_THEME: WidgetTheme = {
    primary: '#a8c030',
    primaryHover: '#8eab24',
    primaryBorder: '#d9e0b8',
    primaryContrast: '#111111',
    primaryLight: '#f4f8e6',
    primaryActive: '#eef4d4',
    primaryDark: '#3f4a2a',
    primaryShadow: 'rgba(142, 171, 36, 0.35)',
    primaryShadowHover: 'rgba(142, 171, 36, 0.45)',
    primaryRing: 'rgba(168, 192, 48, 0.25)',
};

/**
 * Normalizes a user-supplied color code.
 * Supports hex (with or without leading #, 3, 4, 6, or 8 digits),
 * rgb/rgba, hsl/hsla, and common named CSS colors.
 */
export function normalizeColor(value: unknown): string {
    if (typeof value !== 'string') {
        return DEFAULT_COLOR;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return DEFAULT_COLOR;
    }

    // Bare hex without hash (e.g., '1a73e8' or 'fff')
    if (/^[0-9a-fA-F]{3,8}$/.test(trimmed)) {
        return `#${trimmed.toLowerCase()}`;
    }

    // Hex with hash (e.g., '#1a73e8')
    if (/^#[0-9a-fA-F]{3,8}$/.test(trimmed)) {
        return trimmed.toLowerCase();
    }

    // Functional CSS color notation
    if (/^(rgb|rgba|hsl|hsla)\s*\(.*\)$/i.test(trimmed)) {
        return trimmed;
    }

    // Standard CSS color names (alphabetic only, e.g., 'navy', 'teal', 'blue')
    if (/^[a-zA-Z]+$/.test(trimmed)) {
        return trimmed.toLowerCase();
    }

    return DEFAULT_COLOR;
}

export function parseColorToRgb(color: string): { r: number; g: number; b: number } | null {
    const trimmed = color.trim().toLowerCase();

    const hexMatch = /^#?([0-9a-f]{3,8})$/i.exec(trimmed);
    if (hexMatch) {
        const hex = hexMatch[1];
        if (hex.length === 3 || hex.length === 4) {
            return {
                r: parseInt(hex[0] + hex[0], 16),
                g: parseInt(hex[1] + hex[1], 16),
                b: parseInt(hex[2] + hex[2], 16),
            };
        }
        if (hex.length === 6 || hex.length === 8) {
            return {
                r: parseInt(hex.slice(0, 2), 16),
                g: parseInt(hex.slice(2, 4), 16),
                b: parseInt(hex.slice(4, 6), 16),
            };
        }
    }

    const rgbMatch = /^rgba?\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i.exec(trimmed);
    if (rgbMatch) {
        return {
            r: Math.min(255, Math.max(0, parseInt(rgbMatch[1], 10))),
            g: Math.min(255, Math.max(0, parseInt(rgbMatch[2], 10))),
            b: Math.min(255, Math.max(0, parseInt(rgbMatch[3], 10))),
        };
    }

    // Fallback for named colors or other formats in DOM environments
    if (typeof document !== 'undefined') {
        const div = document.createElement('div');
        div.style.color = trimmed;
        if (div.style.color) {
            const computedMatch = /^rgba?\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i.exec(div.style.color);
            if (computedMatch) {
                return {
                    r: parseInt(computedMatch[1], 10),
                    g: parseInt(computedMatch[2], 10),
                    b: parseInt(computedMatch[3], 10),
                };
            }
        }
    }

    return null;
}

export function getRelativeLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map((val) => {
        const s = val / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

export function resolveTheme(color?: string): WidgetTheme {
    const normalized = normalizeColor(color);

    if (normalized === DEFAULT_COLOR) {
        return DEFAULT_THEME;
    }

    const rgb = parseColorToRgb(normalized);
    if (!rgb) {
        return DEFAULT_THEME;
    }

    const { r, g, b } = rgb;
    const lum = getRelativeLuminance(r, g, b);

    // WCAG contrast check: text on primary button/badge
    const primaryContrast = lum > 0.45 ? '#111111' : '#ffffff';

    // Hover state: lighten if very dark, darken if light
    const hoverFactor = lum < 0.2 ? 0.16 : -0.14;
    const hr = Math.min(255, Math.max(0, Math.round(hoverFactor > 0 ? r + (255 - r) * hoverFactor : r * (1 + hoverFactor))));
    const hg = Math.min(255, Math.max(0, Math.round(hoverFactor > 0 ? g + (255 - g) * hoverFactor : g * (1 + hoverFactor))));
    const hb = Math.min(255, Math.max(0, Math.round(hoverFactor > 0 ? b + (255 - b) * hoverFactor : b * (1 + hoverFactor))));
    const primaryHover = `rgb(${hr}, ${hg}, ${hb})`;

    // Subtle dark color for headers, labels, and accents
    const primaryDark = lum < 0.25
        ? `rgb(${r}, ${g}, ${b})`
        : `rgb(${Math.round(r * 0.4)}, ${Math.round(g * 0.4)}, ${Math.round(b * 0.4)})`;

    return {
        primary: normalized,
        primaryHover,
        primaryBorder: `rgba(${r}, ${g}, ${b}, 0.26)`,
        primaryContrast,
        primaryLight: `rgba(${r}, ${g}, ${b}, 0.08)`,
        primaryActive: `rgba(${r}, ${g}, ${b}, 0.16)`,
        primaryDark,
        primaryShadow: `rgba(${r}, ${g}, ${b}, 0.35)`,
        primaryShadowHover: `rgba(${r}, ${g}, ${b}, 0.45)`,
        primaryRing: `rgba(${r}, ${g}, ${b}, 0.25)`,
    };
}

export function applyTheme(element: HTMLElement, color?: string): void {
    const theme = resolveTheme(color);

    element.style.setProperty('--inclusive-primary', theme.primary);
    element.style.setProperty('--inclusive-primary-hover', theme.primaryHover);
    element.style.setProperty('--inclusive-primary-border', theme.primaryBorder);
    element.style.setProperty('--inclusive-primary-text', theme.primaryContrast);
    element.style.setProperty('--inclusive-primary-contrast', theme.primaryContrast);
    element.style.setProperty('--inclusive-primary-light', theme.primaryLight);
    element.style.setProperty('--inclusive-primary-active', theme.primaryActive);
    element.style.setProperty('--inclusive-primary-dark', theme.primaryDark);
    element.style.setProperty('--inclusive-primary-shadow', theme.primaryShadow);
    element.style.setProperty('--inclusive-primary-shadow-hover', theme.primaryShadowHover);
    element.style.setProperty('--inclusive-primary-ring', theme.primaryRing);
}
